import * as THREE from 'three';
import { UINodeConfig, UIComponentConfig, LayoutRect } from './types';
import { measure, layout } from './layout';
import { paintTree } from './painter';

const MAX_CANVAS_SIZE = 1024;

// Shared offscreen canvas used ONLY for text measurement.
// Never resized, so ctx.measureText() always returns stable results.
let _measureCanvas: HTMLCanvasElement | null = null;
let _measureCtx: CanvasRenderingContext2D | null = null;
function getMeasureCtx(): CanvasRenderingContext2D {
  if (!_measureCtx) {
    _measureCanvas = document.createElement('canvas');
    _measureCanvas.width = 1;
    _measureCanvas.height = 1;
    _measureCtx = _measureCanvas.getContext('2d')!;
  }
  return _measureCtx;
}

interface ResolvedConfig {
  worldWidth: number | null;
  worldHeight: number | null;
  mode: 'sprite' | 'mesh';
  resolution: number;
  renderOrder: number;
  depthTest: boolean;
  depthWrite: boolean;
  spriteCenter: { x: number; y: number } | null;
}

export class TSS {
  readonly object3D: THREE.Sprite | THREE.Mesh;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private texture: THREE.CanvasTexture;
  private tree: UINodeConfig;
  private config: ResolvedConfig;
  private dirty = true;
  private rects = new Map<UINodeConfig, LayoutRect>();
  private nodeByTag = new Map<string, UINodeConfig>();

  constructor(tree: UINodeConfig, config: UIComponentConfig) {
    this.tree = tree;
    this.config = {
      worldWidth: config.worldWidth ?? null,
      worldHeight: config.worldHeight ?? null,
      mode: config.mode ?? 'sprite',
      resolution: config.resolution ?? 1,
      renderOrder: config.renderOrder ?? 0,
      depthTest: config.depthTest ?? false,
      depthWrite: config.depthWrite ?? false,
      spriteCenter: config.spriteCenter ?? null,
    };

    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('TSS: Canvas 2D context unavailable');
    }
    this.ctx = ctx;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    // The 2D canvas is already sRGB — tag it so the sRGB-output renderer doesn't
    // double-encode it (which lifted midtones → "too bright" photos).
    this.texture.colorSpace = THREE.SRGBColorSpace;

    if (this.config.mode === 'mesh') {
      // Initial size; will be corrected on first render()
      const initW = this.config.worldWidth ?? 1;
      const initH = this.config.worldHeight ?? 1;
      const geometry = new THREE.PlaneGeometry(initW, initH);
      const material = new THREE.MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        // UI is drawn at final colors — bypass the scene's ACES tone mapping so
        // it renders 1:1 with the canvas instead of being brightened/shifted.
        toneMapped: false,
        depthTest: this.config.depthTest,
        depthWrite: this.config.depthWrite,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.renderOrder = this.config.renderOrder;
      this.object3D = mesh;
    } else {
      const material = new THREE.SpriteMaterial({
        map: this.texture,
        transparent: true,
        toneMapped: false,
        depthTest: this.config.depthTest,
        depthWrite: this.config.depthWrite,
      });
      const sprite = new THREE.Sprite(material);
      sprite.renderOrder = this.config.renderOrder;
      if (this.config.spriteCenter) {
        sprite.center.set(this.config.spriteCenter.x, this.config.spriteCenter.y);
      }
      this.object3D = sprite;
    }

    this.buildTagIndex(this.tree);
    this.render();
  }

  setTree(tree: UINodeConfig): void {
    this.tree = tree;
    this.nodeByTag.clear();
    this.buildTagIndex(this.tree);
    this.dirty = true;
  }

  update(tag: string, patch: Partial<UINodeConfig>): void {
    const node = this.nodeByTag.get(tag);
    if (!node) return;

    let changed = false;

    if (patch.text !== undefined && patch.text !== node.text) {
      node.text = patch.text;
      changed = true;
    }

    if (patch.style) {
      const nextStyle = { ...(node.style ?? {}), ...patch.style };
      const prevStyle = node.style ?? {};
      const keys = new Set([...Object.keys(prevStyle), ...Object.keys(patch.style)]);
      for (const key of keys) {
        if ((prevStyle as Record<string, unknown>)[key] !== (nextStyle as Record<string, unknown>)[key]) {
          changed = true;
          break;
        }
      }
      node.style = nextStyle;
    }

    if (patch.circle) {
      const nextCircle = { ...(node.circle ?? {}), ...patch.circle };
      const prevCircle = node.circle ?? {};
      const keys = new Set([...Object.keys(prevCircle), ...Object.keys(patch.circle)]);
      for (const key of keys) {
        if ((prevCircle as Record<string, unknown>)[key] !== (nextCircle as Record<string, unknown>)[key]) {
          changed = true;
          break;
        }
      }
      node.circle = nextCircle;
    }

    if (patch.children !== undefined && patch.children !== node.children) {
      node.children = patch.children;
      changed = true;
      this.nodeByTag.clear();
      this.buildTagIndex(this.tree);
    }

    if (changed) {
      this.dirty = true;
    }
  }

  render(): void {
    if (!this.dirty) return;

    // Measure on a dedicated context that never gets resized.
    // This guarantees stable measureText() results across renders.
    const measureCtx = getMeasureCtx();
    const sizes = measure(this.tree, measureCtx);
    const rootSize = sizes.get(this.tree);
    if (!rootSize) return;

    // Canvas = measured content size (resolution is DPI multiplier, typically 1)
    const res = this.config.resolution;
    const canvasWidth = Math.min(Math.ceil(rootSize.width * res), MAX_CANVAS_SIZE);
    const canvasHeight = Math.min(Math.ceil(rootSize.height * res), MAX_CANVAS_SIZE);

    // CRITICAL: On WebGL2, Three.js allocates GPU textures via texStorage2D which is
    // immutable — the GPU texture dimensions are locked on first upload. If we resize the
    // canvas and only set needsUpdate, texSubImage2D uploads new content into the old-sized
    // GPU texture, causing stretching. When dimensions change we must dispose the old
    // texture and create a fresh one so texStorage2D re-allocates at the new size.
    const dimensionsChanged = this.canvas.width !== canvasWidth || this.canvas.height !== canvasHeight;

    if (dimensionsChanged) {
      this.canvas.width = canvasWidth;
      this.canvas.height = canvasHeight;
      // Context is wiped by canvas resize — re-acquire
      this.ctx = this.canvas.getContext('2d')!;

      // Dispose old GPU texture and create a new one at the correct dimensions
      this.texture.dispose();
      this.texture = new THREE.CanvasTexture(this.canvas);
      this.texture.minFilter = THREE.LinearFilter;
      this.texture.magFilter = THREE.LinearFilter;
      this.texture.colorSpace = THREE.SRGBColorSpace;

      // Point the material at the new texture
      const material = (this.object3D as THREE.Sprite | THREE.Mesh).material as
        THREE.SpriteMaterial | THREE.MeshBasicMaterial;
      material.map = this.texture;
    }

    // Layout into measured root size — consistent with the sizes map
    this.rects = layout(this.tree, sizes, {
      x: 0,
      y: 0,
      width: rootSize.width,
      height: rootSize.height,
    });

    // Paint: scale from layout coords → canvas pixels
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    const scale = res; // With res=1, no scaling needed
    this.ctx.setTransform(scale, 0, 0, scale, 0, 0);
    paintTree(this.ctx, this.tree, this.rects, scale);
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);

    this.texture.needsUpdate = true;

    // Derive world dimensions — auto-derive whichever is missing from canvas aspect
    const aspect = this.canvas.width / Math.max(1, this.canvas.height);
    let worldWidth: number;
    let worldHeight: number;
    if (this.config.worldWidth != null && this.config.worldHeight != null) {
      // Both explicit — use as-is (caller accepts potential stretching)
      worldWidth = this.config.worldWidth;
      worldHeight = this.config.worldHeight;
    } else if (this.config.worldWidth != null) {
      // Width given, derive height from aspect
      worldWidth = this.config.worldWidth;
      worldHeight = worldWidth / aspect;
    } else if (this.config.worldHeight != null) {
      // Height given, derive width from aspect (auto-fit width)
      worldHeight = this.config.worldHeight;
      worldWidth = worldHeight * aspect;
    } else {
      // Neither given — use canvas pixels as world units (1px = 1 unit)
      worldWidth = this.canvas.width;
      worldHeight = this.canvas.height;
    }

    if (this.object3D instanceof THREE.Sprite) {
      this.object3D.scale.set(worldWidth, worldHeight, 1);
    } else {
      const mesh = this.object3D as THREE.Mesh;
      const geometry = mesh.geometry as THREE.PlaneGeometry;
      if (geometry.parameters.width !== worldWidth || geometry.parameters.height !== worldHeight) {
        geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(worldWidth, worldHeight);
      }
    }

    this.dirty = false;
  }

  hitTest(canvasX: number, canvasY: number): string | null {
    const ordered: UINodeConfig[] = [];
    const collect = (current: UINodeConfig): void => {
      ordered.push(current);
      if (current.children) {
        for (const child of current.children) {
          collect(child);
        }
      }
    };
    collect(this.tree);

    for (let i = ordered.length - 1; i >= 0; i--) {
      const node = ordered[i];
      if (!node.tag) continue;
      const rect = this.rects.get(node);
      if (!rect) continue;
      if (
        canvasX >= rect.x &&
        canvasX <= rect.x + rect.width &&
        canvasY >= rect.y &&
        canvasY <= rect.y + rect.height
      ) {
        return node.tag;
      }
    }

    return null;
  }

  uvToCanvas(uv: THREE.Vector2): { x: number; y: number } {
    return { x: uv.x * this.canvas.width, y: (1 - uv.y) * this.canvas.height };
  }

  setVisible(visible: boolean): void {
    this.object3D.visible = visible;
  }

  reset(): void {
    this.object3D.visible = false;
    this.object3D.parent?.remove(this.object3D);
  }

  attachTo(parent: THREE.Object3D): void {
    parent.add(this.object3D);
  }

  setPosition(x: number, y: number, z: number): void {
    this.object3D.position.set(x, y, z);
  }

  dispose(): void {
    // Detach from the scene graph FIRST. Freeing the texture/material/geometry
    // below without removing the object leaves a parented mesh with a disposed
    // material — which renders as a black quad. This bit the pooled cee-lo
    // banker zone: removeZone() disposed the lock overlay but the overlay mesh
    // stayed a child of the pooled zone group and reappeared (black plane) on
    // reuse. reset() already detaches; dispose() must too.
    this.object3D.parent?.remove(this.object3D);

    this.texture.dispose();

    if (this.object3D instanceof THREE.Sprite) {
      (this.object3D.material as THREE.SpriteMaterial).dispose();
    } else {
      const mesh = this.object3D as THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.MeshBasicMaterial).dispose();
    }

    this.nodeByTag.clear();
    this.rects.clear();
    this.canvas.width = 0;
    this.canvas.height = 0;
  }

  private buildTagIndex(node: UINodeConfig): void {
    if (node.tag) {
      this.nodeByTag.set(node.tag, node);
    }
    if (node.children) {
      for (const child of node.children) {
        this.buildTagIndex(child);
      }
    }
  }
}
